
import * as React from 'react';
import { useState } from 'react';
import AdminSidebar from '../components/AdminSidebar';
import AdminHeader from '../components/AdminHeader';
import FAQList from './components/FAQList';
import AddFAQModal from './components/AddFAQModal';
import EditFAQModal from './components/EditFAQModal';
import DeleteConfirmModal from './components/DeleteConfirmModal';



/* -------------------------------------------------
   Initial data – kept separate so it can be reused
   or fetched from an API in the future.
--------------------------------------------------- */
const initialFAQs = [
  {
    id: 1,
    question: 'What is your return policy?',
    answer:
      "We offer a 3-day return policy for all unworn items with original tags attached. Simply contact our customer service team to initiate a return, and we'll provide you with a prepaid shipping label."
  },
  {
    id: 2,
    question: 'How long does shipping take?',
    answer:
      'Standard shipping typically takes 5-7 business days. Express shipping options are available at checkout for delivery within 2-3 business days. Free shipping is available on orders over N30,000.'
  },
  {
    id: 3,
    question: 'Do you ship internationally?',
    answer:
      'Yes! We ship to over 50 countries worldwide. International shipping times vary by location but typically take 10-15 business days. Customs fees may apply depending on your country.'
  },
  {
    id: 4,
    question: 'How do I track my order?',
    answer:
      "Once your order ships, you'll receive a tracking number via email. You can use this number to track your package on our website or the carrier's website. You can also check your order status by logging into your account."
  },
  {
    id: 5,
    question: 'What payment methods do you accept?',
    answer:
      'We accept all major credit cards (Visa, Mastercard, American Express, Discover), PayPal, Apple Pay, and Google Pay. All transactions are secured with SSL encryption for your safety.'
  },
  {
    id: 6,
    question: 'How do I find my size?',
    answer:
      "Each product page includes a detailed size chart. We recommend measuring yourself and comparing to our size guide. If you're between sizes, we suggest sizing up for a more comfortable fit. Our customer service team is also happy to help with sizing questions."
  }
];

export default function AdminFAQPage() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [faqs, setFaqs] = useState(initialFAQs);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingFAQ, setEditingFAQ] = useState(null);
  const [deletingFAQ, setDeletingFAQ] = useState(null);

  /* -------------------------------------------------
     Add a new FAQ
     Basic validation + defensive error handling
  --------------------------------------------------- */
  const handleAddFAQ = (question, answer) => {
    if (!question.trim() || !answer.trim()) {
      alert('Both question and answer are required.');
      return;
    }

    const newId = faqs.length ? Math.max(...faqs.map(f => f.id)) + 1 : 1;
    const newFAQ = { id: newId, question: question.trim(), answer: answer.trim() };
    setFaqs(prev => [...prev, newFAQ]);
    setIsAddModalOpen(false);
  };

  /* -------------------------------------------------
     Edit an existing FAQ
  --------------------------------------------------- */
  const handleEditFAQ = (id, question, answer) => {
    if (!question.trim() || !answer.trim()) {
      alert('Both question and answer are required.');
      return;
    }

    setFaqs(prev =>
      prev.map(faq => (faq.id === id ? { ...faq, question: question.trim(), answer: answer.trim() } : faq))
    );
    setEditingFAQ(null);
  };

  /* -------------------------------------------------
     Delete an FAQ
  --------------------------------------------------- */
  const handleDeleteFAQ = (id) => {
    setFaqs(prev => prev.filter(faq => faq.id !== id));
    setDeletingFAQ(null);
  };

  /* -------------------------------------------------
     Reorder FAQs (drag‑and‑drop)
  --------------------------------------------------- */
  const handleReorder = (fromIndex, toIndex) => {
    if (fromIndex < 0 || toIndex < 0 || fromIndex >= faqs.length || toIndex >= faqs.length) {
      console.warn('Invalid reorder indices', { fromIndex, toIndex });
      return;
    }
    const reordered = [...faqs];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);
    setFaqs(reordered);
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] font-['Poppins']">
      {/* Mobile Sidebar Overlay */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      <AdminSidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <div className="lg:ml-64 min-h-screen">
        <AdminHeader onMenuClick={() => setIsSidebarOpen(true)} />

        <main className="p-4 md:p-6 lg:p-8">
          <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                FAQ Management
              </h1>
              <p className="text-sm text-gray-600">Manage frequently asked questions</p>
            </div>
            <button
              onClick={() => setIsAddModalOpen(true)}
              className="px-6 py-3 bg-[#EF579A] text-white rounded-lg hover:bg-[#d94a87] transition-colors flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-add-line text-xl" />
              Add FAQ
            </button>
          </div>

          <FAQList
            faqs={faqs}
            onEdit={setEditingFAQ}
            onDelete={setDeletingFAQ}
            onReorder={handleReorder}
          />
        </main>
      </div>

      {/* -------------------- Modals -------------------- */}
      <AddFAQModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onAdd={handleAddFAQ}
      />

      {editingFAQ && (
        <EditFAQModal
          faq={editingFAQ}
          onClose={() => setEditingFAQ(null)}
          onSave={handleEditFAQ}
        />
      )}

      {deletingFAQ && (
        <DeleteConfirmModal
          faqQuestion={deletingFAQ.question}
          onClose={() => setDeletingFAQ(null)}
          onConfirm={() => handleDeleteFAQ(deletingFAQ.id)}
        />
      )}
    </div>
  );
}
